import { Component } from '@theme/component';

/**
 * List Obat Component
 * Handles alphabet navigation and filtering of medicine articles
 */
class ListObatComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    /** @type {NodeListOf<HTMLButtonElement>} */
    this.letterButtons = this.querySelectorAll('[data-letter]');
    
    /** @type {NodeListOf<HTMLElement>} */
    this.letterContents = this.querySelectorAll('[data-letter-content]');
    
    /** @type {string} */
    this.currentLetter = this.dataset.defaultLetter || 'A';
    
    this.setupEventListeners();
    this.setupUrlHash();
    this.mergeRemainingPages();
  }

  /**
   * Liquid can only paginate 250 articles per request. The server renders one page
   * (with crawlable links to the others); here we fetch the remaining pages through the
   * Section Rendering API and merge their entries into the letter groups, so visitors
   * see one complete A–Z list.
   */
  async mergeRemainingPages() {
    const pages = parseInt(this.dataset.pages || '1', 10);
    const current = parseInt(this.dataset.page || '1', 10);
    const sectionId = this.dataset.sectionId;
    const baseUrl = this.dataset.baseUrl;
    if (!sectionId || !baseUrl || Number.isNaN(pages) || pages <= 1) return;

    const status = this.querySelector('[data-list-obat-status]');
    const pagination = this.querySelector('[data-list-obat-pagination]');
    const setStatus = (text) => {
      if (!status) return;
      status.textContent = text;
      status.hidden = !text;
    };
    setStatus(this.dataset.loadingText || '');

    /** @type {Map<string, HTMLElement>} */
    const groups = new Map();
    this.querySelectorAll('[data-letter-content]').forEach((el) => {
      groups.set(el.dataset.letterContent || '', /** @type {HTMLElement} */ (el));
    });
    const seen = new Set([...this.querySelectorAll('.list-obat__item')].map((a) => a.getAttribute('href')));

    const pageNumbers = [];
    for (let i = 1; i <= pages; i += 1) if (i !== current) pageNumbers.push(i);

    const results = await Promise.allSettled(
      pageNumbers.map(async (page) => {
        const url = `${baseUrl}?section_id=${encodeURIComponent(sectionId)}&page=${page}`;
        const res = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(`page ${page}: ${res.status}`);
        return res.text();
      })
    );

    let failed = 0;
    results.forEach((result) => {
      if (result.status !== 'fulfilled') {
        failed += 1;
        return;
      }
      const doc = new DOMParser().parseFromString(result.value, 'text/html');
      doc.querySelectorAll('[data-letter-content]').forEach((remoteGroup) => {
        const letter = remoteGroup.getAttribute('data-letter-content') || '';
        const target = groups.get(letter);
        if (!target) return;
        remoteGroup.querySelectorAll('.list-obat__item').forEach((link) => {
          const href = link.getAttribute('href');
          if (!href || seen.has(href)) return;
          seen.add(href);
          target.appendChild(document.importNode(link, true));
        });
      });
    });

    // Sort each letter alphabetically, drop the "no entries" note where entries arrived,
    // and enable letter buttons that gained entries.
    groups.forEach((group, letter) => {
      const items = [...group.querySelectorAll('.list-obat__item')];
      if (!items.length) return;
      items.sort((a, b) => (a.textContent || '').trim().localeCompare((b.textContent || '').trim(), 'id'));
      items.forEach((item) => group.appendChild(item));
      group.querySelectorAll('.list-obat__empty').forEach((el) => el.remove());

      const disabledForLetter = [...this.querySelectorAll('.list-obat__letter--disabled')].find(
        (el) => (el.textContent || '').trim() === letter
      );
      if (disabledForLetter) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'list-obat__letter';
        button.dataset.letter = letter;
        button.setAttribute('aria-pressed', 'false');
        button.textContent = letter;
        button.addEventListener('click', () => {
          this.showLetter(letter);
          this.updateUrl(letter);
        });
        disabledForLetter.replaceWith(button);
      }
    });
    this.letterButtons = this.querySelectorAll('[data-letter]');

    if (failed === 0) {
      if (pagination) pagination.hidden = true;
      setStatus(this.dataset.loadedText || '');
    } else {
      // Keep the server pagination visible so every entry stays reachable.
      setStatus('');
    }
  }
  
  /**
   * Setup event listeners for letter buttons
   */
  setupEventListeners() {
    if (!this.letterButtons) return;
    
    this.letterButtons.forEach(button => {
      button.addEventListener('click', (/** @type {Event} */ e) => {
        const target = /** @type {HTMLButtonElement} */ (e.currentTarget);
        const letter = target.dataset.letter;
        
        if (letter) {
          this.showLetter(letter);
          this.updateUrl(letter);
        }
      });
    });
  }
  
  /**
   * Setup URL hash handling for deep linking
   */
  setupUrlHash() {
    // Check if there's a hash in URL
    const hash = window.location.hash.slice(1); // Remove #
    
    if (hash && hash.length === 1) {
      const letter = hash.toUpperCase();
      
      // Check if this letter has a button (meaning it has articles)
      const button = this.querySelector(`[data-letter="${letter}"]`);
      
      if (button) {
        this.showLetter(letter);
      }
    }
    
    // Listen for hash changes
    window.addEventListener('hashchange', () => {
      const hash = window.location.hash.slice(1);
      
      if (hash && hash.length === 1) {
        const letter = hash.toUpperCase();
        const button = this.querySelector(`[data-letter="${letter}"]`);
        
        if (button) {
          this.showLetter(letter);
        }
      }
    });
  }
  
  /**
   * Show articles for a specific letter
   * @param {string} letter - The letter to show
   */
  showLetter(letter) {
    if (this.currentLetter === letter) return;
    
    this.currentLetter = letter;
    
    // Update button states
    if (this.letterButtons) {
      this.letterButtons.forEach(button => {
      const isActive = button.dataset.letter === letter;
      
      if (isActive) {
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.classList.remove('active');
        button.setAttribute('aria-pressed', 'false');
      }
    });
    }
    
    // Update content visibility
    if (this.letterContents) {
      this.letterContents.forEach(content => {
      const isActive = content.dataset.letterContent === letter;
      
      if (isActive) {
        content.classList.add('active');
        content.style.display = 'grid';
        
        // Trigger animation
        requestAnimationFrame(() => {
          content.style.opacity = '1';
          content.style.visibility = 'visible';
        });
      } else {
        content.classList.remove('active');
        content.style.display = 'none';
        content.style.opacity = '0';
        content.style.visibility = 'hidden';
      }
    });
    }
    
    // Scroll to top of content (smooth)
    const content = this.querySelector('.list-obat__content');
    if (content) {
      const rect = content.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Only scroll if content is above viewport
      if (rect.top < 100) {
        window.scrollTo({
          top: scrollTop + rect.top - 100,
          behavior: 'smooth'
        });
      }
    }
  }
  
  /**
   * Update URL hash without triggering page reload
   * @param {string} letter - The letter to set in URL
   */
  updateUrl(letter) {
    // Use replaceState to avoid adding to browser history for every click
    const newUrl = `${window.location.pathname}${window.location.search}#${letter}`;
    
    if (window.location.hash !== `#${letter}`) {
      history.replaceState(null, '', newUrl);
    }
  }
}

customElements.define('list-obat-component', ListObatComponent);
